import {computed, EffectRef, Injectable, Signal, WritableSignal, effect, inject, signal, untracked} from '@angular/core';
import {LocalStorageService} from "./local-storage.service";
import {Params} from "@angular/router";
import {TYPE} from "../types/types";
import {ApiService} from './api.service';
import {CustomerPublicResource} from '../types/settings-types/customerPublicResource';
import {Observable, take, shareReplay} from 'rxjs';
import {CustomerResource} from '../types/settings-types/customerResource';
import {TokenService} from './token.service';
import {environment} from '../../../environments/environment';

export interface CustomerAuthContext {
  modules: Record<string, unknown> & {
    setting_employees_detail_groups_show: boolean;
    setting_logout_redirect: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class CustomerService {
  private localStorageService: LocalStorageService = inject(LocalStorageService);
  private apiService: ApiService = inject(ApiService);
  private tokenService: TokenService = inject(TokenService);

  private _customerKey: WritableSignal<string | null> = signal(null);
  private _publicCustomerResource: WritableSignal<CustomerPublicResource | null> = signal(null);
  private _customerResource: WritableSignal<CustomerResource | null> = signal(null);
  private _customerContext: WritableSignal<CustomerAuthContext> = signal(this.createDefaultCustomerContext());
  private ssoOptionsSignal: Signal<TYPE.SsoOption[]> = computed(() =>
    this.mapSsoOptions(this._publicCustomerResource()?.sso ?? null)
  );
  
  private publicCustomerResourceCache: Map<string, Observable<CustomerPublicResource>> = new Map<string, Observable<CustomerPublicResource>>();
  private readonly reservedSubdomains: Set<string> = new Set<string>(['web', 'stage']);

  public customerKey: Signal<string | null> = this._customerKey.asReadonly();
  public publicCustomerResource: Signal<CustomerPublicResource | null> = this._publicCustomerResource.asReadonly();
  public customerResource: Signal<CustomerResource | null> = this._customerResource.asReadonly();
  public customerContext: Signal<CustomerAuthContext> = this._customerContext.asReadonly();

  public constructor() {
    this.setCustomerKey(this.getStoredCustomerKey());
  }

  public setCustomerKey(key: string | null, persist: boolean = true): void {
    const normalizedKey = this.normalizeCustomerKey(key);

    if (persist && normalizedKey) {
      this.localStorageService.setLocalStorage('customerKey', normalizedKey);
    }

    if(this._customerKey() !== normalizedKey) {
      this._publicCustomerResource.set(null);
      this._customerContext.set(this.createDefaultCustomerContext());
      this._customerResource.set(null);
      this._customerKey.set(normalizedKey);
    }
  }

  public reloadCustomerData(): void {
    const key = this._customerKey();
    if (key !== null) {
      this.publicCustomerResourceCache.delete(key);
    }

    this.loadPublicCustomerData(key);
    this.loadCustomerData(key);
  }

  private publicCustomerEffect: EffectRef = effect(() => {
    const key = this._customerKey();
    untracked(() => {
      this.loadPublicCustomerData(key);
    });
  });

  private customerEffect: EffectRef = effect(() => {
    if(this.tokenService.accessToken().trim() !== '') {
      this.loadCustomerData(this._customerKey());
    }
  });

  public getCustomerKey(): string | null {
    return this._customerKey() ?? this.normalizeCustomerKey(this.localStorageService.getLocalStorage('customerKey'));
  }

  public setPublicCustomerResource(resource: CustomerPublicResource | null): void {
    this._publicCustomerResource.set(resource);
    this._customerContext.set(this.mapPublicResourceToContext(resource));
  }

  public hasSsoOptions(): boolean {
    const opts = this.ssoOptionsSignal();
    return Array.isArray(opts) && opts.some(o => !!o?.auth_url);
  }

  public getSsoOptions(): TYPE.SsoOption[] {
    return this.ssoOptionsSignal();
  }

  public resetAuthenticatedCustomerData(): void {
    this._customerResource.set(null);
  }

  public resetCustomer(): void {
    this._customerKey.set(null);
    this._publicCustomerResource.set(null);
    this._customerContext.set(this.createDefaultCustomerContext());
    this._customerResource.set(null);
    this.localStorageService.removeItem('customerKey');
    this.publicCustomerResourceCache.clear();
  }
  
  public getPublicCustomerResourceCached(customerKey: string): Observable<CustomerPublicResource> {
    if (!this.publicCustomerResourceCache.has(customerKey)) {
      const request$ = this.apiService.getPublicCustomerResource(customerKey).pipe(
        take(1),
        shareReplay(1)
      );
      this.publicCustomerResourceCache.set(customerKey, request$);
    }
    return this.publicCustomerResourceCache.get(customerKey)!;
  }

  public getStoredCustomerKey(params?: Params): string | null {
    const customerKeyFromParams = this.getCustomerKeyFromParams(params);
    if (customerKeyFromParams) {
      return customerKeyFromParams;
    }

    const customerKeyFromHostname = this.getCustomerKeyFromHostname();
    if (customerKeyFromHostname) {
      return customerKeyFromHostname;
    }

    const customerKeyFromStorage = this.normalizeCustomerKey(this.localStorageService.getLocalStorage('customerKey'));
    if (customerKeyFromStorage) {
      return customerKeyFromStorage;
    }

    return null;
  }

  public isCustomerHostRoutingEnabled(): boolean {
    return environment.customerHostRouting.enabled;
  }

  public redirectToCustomerHost(customerKey: string, target: string): boolean {
    const customerUrl = this.buildCustomerHostUrl(customerKey, target);
    if (!customerUrl || this.isCurrentOrigin(customerUrl)) {
      return false;
    }

    this.navigateBrowserTo(customerUrl.toString());
    return true;
  }

  public redirectToDefaultCustomerHost(target: string): boolean {
    const customerUrl = this.buildCustomerHostUrl(environment.customerHostRouting.defaultSubdomain, target);
    if (!customerUrl || this.isCurrentOrigin(customerUrl)) {
      return false;
    }

    this.navigateBrowserTo(customerUrl.toString());
    return true;
  }

  private getCustomerKeyFromParams(params?: Params): string | null {
    if (!params || !Object.prototype.hasOwnProperty.call(params, 'customerKey')) {
      return null;
    }

    return this.normalizeCustomerKey(params['customerKey']);
  }

  private getCustomerKeyFromHostname(): string | null {
    return this.resolveCustomerKeyFromHostname(window.location.hostname);
  }

  private resolveCustomerKeyFromHostname(hostname: string): string | null {
    if (!this.isCustomerHostRoutingEnabled()) {
      return null;
    }

    const normalizedHostname = (hostname ?? '').trim().toLowerCase();
    if (!normalizedHostname) {
      return null;
    }

    // Development hosts should not force a customer key.
    if (
      normalizedHostname === 'localhost' ||
      normalizedHostname === '127.0.0.1' ||
      normalizedHostname === '::1' ||
      !normalizedHostname.includes('.') ||
      normalizedHostname.endsWith('.local') ||
      normalizedHostname.endsWith('.internal')
    ) {
      return null;
    }

    const baseDomain = environment.customerHostRouting.baseDomain;
    const productionHost = `${environment.customerHostRouting.defaultSubdomain}.${baseDomain}`;
    if (normalizedHostname === productionHost) {
      return null;
    }

    const suffix = `.${baseDomain}`;
    if (normalizedHostname.endsWith(suffix)) {
      const subdomain = normalizedHostname.slice(0, -suffix.length);
      if (!subdomain) {
        return null;
      }

      const reservedSubdomains = new Set([
        ...this.reservedSubdomains,
        environment.customerHostRouting.defaultSubdomain
      ]);
      if (reservedSubdomains.has(subdomain)) {
        return null;
      }

      return subdomain;
    }

    return null;
  }

  private buildCustomerHostUrl(customerKey: string, target: string): URL | null {
    if (!this.isCustomerHostRoutingEnabled()) {
      return null;
    }

    const normalizedKey = this.normalizeCustomerKey(customerKey);
    if (!normalizedKey) {
      return null;
    }

    const normalizedTarget = target.startsWith('/') ? target : '/' + target;
    return new URL(normalizedTarget, `${environment.customerHostRouting.protocol}://${normalizedKey}.${environment.customerHostRouting.baseDomain}`);
  }

  private normalizeCustomerKey(key: unknown): string | null {
    if (typeof key !== 'string') {
      return null;
    }

    const trimmedKey = key.trim();
    return trimmedKey.length > 2 ? trimmedKey : null;
  }

  private isCurrentOrigin(url: URL): boolean {
    return window.location.origin.toLowerCase() === url.origin.toLowerCase();
  }

  private navigateBrowserTo(url: string): void {
    window.location.assign(url);
  }

  private loadPublicCustomerData(key: string | null): void {
    if(key === null) {
      this.setPublicCustomerResource(null);
      this.publicCustomerResourceCache.clear();
    } else {
      this.setPublicCustomerResource(null);
      this.getPublicCustomerResourceCached(key)
      .subscribe({
        next: (customerData) => {
          this.setPublicCustomerResource(customerData);
        },
        error: (error) => {
          console.error(error);
          this.setPublicCustomerResource(null);
          this.publicCustomerResourceCache.delete(key);
        }
      });
    }
  }

    private loadCustomerData(key: string | null): void {
    if(key === null) {
      this._customerResource.set(null);
    } else {
      this.apiService.getCustomerResource()
      .pipe(take(1))
      .subscribe({
        next: (customerData) => {
          this._customerResource.set(customerData);
          if (customerData.key) {
            this.localStorageService.setLocalStorage('customerKey', customerData.key);
            this.setCustomerKey(customerData.key);
          }
        },
        error: (error) => {
          console.error(error);
          this._customerResource.set(null);
        }
      });
    }
  }

  private createDefaultCustomerContext(): CustomerAuthContext {
    // TODO(customer-public-resource-migration): Temporary frontend defaults while CustomerPublicResource
    // is missing legacy auth/runtime fields. Remove once backend contract is complete.
    return {
      modules: {
        setting_employees_detail_groups_show: false,
        setting_logout_redirect: ''
      }
    };
  }

  private mapPublicResourceToContext(resource: CustomerPublicResource | null): CustomerAuthContext {
    const defaults = this.createDefaultCustomerContext();
    if (!resource) {
      return defaults;
    }

    // TODO(customer-public-resource-migration): Temporary untyped adapter for partially available fields in
    // CustomerPublicResource. Replace with strict typed mapping and remove casts after backend schema update.
    const raw = resource as unknown as Record<string, unknown>;
    const rawModules = (raw['modules'] as Record<string, unknown> | undefined) ?? {};
    return {
      modules: {
        ...rawModules,
        setting_employees_detail_groups_show: typeof rawModules['setting_employees_detail_groups_show'] === 'boolean' ? rawModules['setting_employees_detail_groups_show'] : defaults.modules.setting_employees_detail_groups_show,
        setting_logout_redirect: typeof rawModules['setting_logout_redirect'] === 'string' ? rawModules['setting_logout_redirect'] : defaults.modules.setting_logout_redirect
      }
    };
  }

  private mapSsoOptions(ssoUrl: string | null): TYPE.SsoOption[] {
    const trimmed = (ssoUrl ?? '').trim();
    if (!trimmed) {
      return [];
    }

    return [{
      type: 'sso',
      button_label: 'SSO',
      auth_url: trimmed
    }];
  }
}
